import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MetricsPanel } from '../../components/client/MetricsPanel'
import { LogsPanel } from '../../components/client/LogsPanel'
import { ConsolePanel } from '../../components/client/ConsolePanel'
import { containerAPI, containersAPI, MyContainer } from '@hpanel/web-common/api/client'

type Tab = 'metrics' | 'logs' | 'console'

const TAB_LABELS: Record<Tab, string> = {
  metrics: 'Metrics',
  logs: 'Logs',
  console: 'Console',
}

export const Container: React.FC = () => {
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') as Tab) || 'metrics'
  const [containers, setContainers] = useState<MyContainer[] | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Restart: single-action confirmation (CLAUDE.md §15 - no fields to fill).
  const [confirmingRestart, setConfirmingRestart] = useState(false)
  const [restarting, setRestarting] = useState(false)
  const [restartResult, setRestartResult] = useState<string | null>(null)
  const [restartError, setRestartError] = useState<string | null>(null)

  useEffect(() => {
    containerAPI.listMine()
      .then(list => {
        setContainers(list)
        if (list.length > 0) setSelectedId(list[0].id)
      })
      .catch(err => setError(err.message))
  }, [])

  const selected = containers?.find(c => c.id === selectedId) ?? null

  const confirmRestart = async () => {
    if (selectedId === null) return
    setRestarting(true)
    setRestartResult(null)
    setRestartError(null)
    try {
      const res = await containersAPI.restart(selectedId)
      setRestartResult(res.message || 'Container restarted')
    } catch (err: unknown) {
      console.error('Container restart failed', err)
      setRestartError(err instanceof Error ? err.message : 'Restart failed')
    } finally {
      setRestarting(false)
      setConfirmingRestart(false)
    }
  }

  const switchTab = (next: Tab) => {
    const p = new URLSearchParams(params)
    p.set('tab', next)
    setParams(p)
  }

  return (
    <div data-testid="container-page" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 data-testid="page-title" className="text-2xl font-bold">My Container</h1>
          <p className="text-gray-600 dark:text-gray-400">Monitor resources, view logs, and access the shell.</p>
        </div>
        {selectedId !== null && (
          <button
            type="button"
            data-testid="container-restart"
            disabled={restarting}
            onClick={() => { setRestartResult(null); setRestartError(null); setConfirmingRestart(true) }}
            className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Restart
          </button>
        )}
      </div>

      {restartResult && (
        <div data-testid="container-restart-result" className="bg-green-50 border border-green-200 rounded p-3 text-green-800">
          {restartResult}
        </div>
      )}
      {restartError && (
        <div data-testid="container-restart-error" className="bg-red-50 border border-red-200 rounded p-3 text-red-700">
          {restartError}
        </div>
      )}

      {confirmingRestart && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="container-restart-confirm"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md space-y-4">
            <h2 className="text-lg font-semibold">Restart container?</h2>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {selected?.name ?? 'Your container'} stops and starts again. Your sites and any
              running console sessions are interrupted for a few seconds.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="container-restart-cancel"
                disabled={restarting}
                onClick={() => setConfirmingRestart(false)}
                className="px-3 py-1 border rounded text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="container-restart-confirm-button"
                disabled={restarting}
                onClick={confirmRestart}
                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded text-sm"
              >
                {restarting ? 'Restarting…' : 'Restart'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div data-testid="container-error" className="bg-red-50 border border-red-200 rounded p-3 text-red-700">
          {error}
        </div>
      )}

      {containers && containers.length > 1 && (
        <select
          data-testid="container-selector"
          value={selectedId ?? ''}
          onChange={e => setSelectedId(Number(e.target.value))}
          className="border rounded px-2 py-1"
        >
          {containers.map(c => (
            <option key={c.id} value={c.id}>{c.name} ({c.backend_kind})</option>
          ))}
        </select>
      )}

      <div role="tablist" className="border-b flex gap-4">
        {(Object.keys(TAB_LABELS) as Tab[]).map(t => (
          <button
            key={t}
            role="tab"
            data-testid={`tab-${t}`}
            aria-selected={tab === t}
            onClick={() => switchTab(t)}
            className={`pb-2 px-1 ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {selectedId !== null && (
        <div role="tabpanel" data-testid={`panel-${tab}`}>
          {tab === 'metrics' && <MetricsPanel containerId={selectedId} />}
          {tab === 'logs' && <LogsPanel containerId={selectedId} />}
          {tab === 'console' && <ConsolePanel containerId={selectedId} />}
        </div>
      )}

      {containers && containers.length === 0 && (
        <div data-testid="no-containers" className="text-gray-600">
          You don't have any containers yet.
        </div>
      )}
    </div>
  )
}

export default Container
